import { hashKey, type QueryClient } from '@tanstack/react-query'
import {
  getClusteredMarkers,
  type ClusterMarker,
  type MapBounds,
} from '@/services/consumerService'
import { tileBounds, tilesCovering, type Grid } from './markerTiles'

type Filters = { name: string; value: string[] }[]

// Cell positions and counts change rarely; alerts and readings come from the
// status request, which is never cached.
const TILE_CACHE_MS = 30 * 60_000

const tileRoot = (accountId: string) => ['mapMarkerTiles', accountId] as const

// Markers for the view, served from whole cached tiles. Tiles that a previous
// view was still loading but this view does not need are aborted.
export const fetchMarkersInView = async (
  client: QueryClient,
  accountId: string,
  box: MapBounds,
  grid: Grid,
  filters: Filters = []
): Promise<ClusterMarker[]> => {
  const { zoom, cells } = grid
  const queries = tilesCovering(box, zoom, cells).map(tile => ({
    queryKey: [...tileRoot(accountId), zoom, cells, filters, tile.x, tile.y],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      getClusteredMarkers(
        accountId,
        tileBounds(tile, zoom, cells),
        zoom,
        filters,
        signal
      ),
    staleTime: TILE_CACHE_MS,
    gcTime: TILE_CACHE_MS,
    retry: false,
  }))
  const wanted = new Set(queries.map(q => hashKey(q.queryKey)))
  // Not awaited: the abort happens now, before this view's own tiles start.
  void client.cancelQueries({
    queryKey: tileRoot(accountId),
    predicate: query => !wanted.has(query.queryHash),
  })
  const tiles = await Promise.all(queries.map(q => client.fetchQuery(q)))
  return tiles.flat()
}

export const cancelMarkerTiles = (client: QueryClient, accountId: string) =>
  client.cancelQueries({ queryKey: tileRoot(accountId) })
